<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { ValidData as AddAddressDialogFormValidData } from '@/components/dialogForms/AddAddressDialogForm.vue';
import type { DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref } from 'vue';
import _cloneDeep from 'lodash/cloneDeep';

import KBtn from '@/components/btns/KBtn.vue';
import LegChooseNewAirRoadStepAdditionalInfoDescList from '@/components/descLists/LegChooseNewAirRoadStepAdditionalInfoDescList.vue';
import WideJobStopEmptyDiagram from '@/components/diagrams/WideJobStopEmptyDiagram.vue';
import KRadioField from '@/components/fields/KRadioField.vue';
import KSingleAutocompleteField from '@/components/fields/KSingleAutocompleteField.vue';
import StepNewAirRoadHeader from '@/components/headers/StepNewAirRoadHeader.vue';
import LegProductsTablesSection from '@/components/sections/LegProductsTablesSection.vue';
import NewAirRoadStopAddressText from '@/components/texts/NewAirRoadStopAddressText.vue';
import { useDialogStore } from '@/stores/useDialogStore';
import * as airRoadHelpers from '@/helpers/airRoad';
import { requiredValidationRule } from '@/helpers/validationRules';
import * as airRoadConstants from '@/constants/airRoad';

export interface AddressItem {
  value: openapi.AirroadSingleCreateBodyOpenapi['ar_stops'][0];
  title: string;
}

export interface Props {
  airRoadBookingJob?: ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi>;
  addresses?: ReadonlyDeep<
    openapi.AddressBookAddressesListContentOpenapi['results']
  >;
}
const props = withDefaults(defineProps<Props>(), {
  airRoadBookingJob: undefined,
  addresses: () => [],
});

const model = defineModel<{
  params: openapi.AirroadSingleCreateQueryOpenapi;
  data: openapi.AirroadSingleCreateBodyOpenapi;
}>({
  default: () => _cloneDeep(airRoadConstants.defaultBookingState),
});

const dialogStore = useDialogStore();

const stops = computed(function () {
  return props.airRoadBookingJob?.stops || [];
});

const stopAddressesItems = computed(function () {
  const _stopAddressesItems: {
    value: openapi.AirroadSingleCreateBodyOpenapi['ar_stops'][0];
    title: string;
  }[][] = [];
  stops.value.forEach(function (stop, stopIndex) {
    _stopAddressesItems[stopIndex] = props.addresses.map(function (address) {
      return _buildAddressItem(address, stop);
    });
    const existedAddress = props.addresses.find(function (address) {
      if (
        address.line1 === stop.address_line_1 &&
        address.line2 === stop.address_line_2 &&
        address.name === stop.name &&
        address.suburb?.name === stop.suburb &&
        // TODO
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        address.suburb?.postcode === stop.post_code && // TODO post_code vs postcode
        // TODO
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        address.suburb?.state === stop.state
      ) {
        return true;
      }
      return false;
    });
    if (!existedAddress) {
      _stopAddressesItems[stopIndex].push({
        value: airRoadHelpers.getAirRoadStopItemValue(stop),
        title: stop.name + ', ' + stop.state,
      });
    }
    const adeddAddressesItem = adeddAddressesItems.value[stopIndex];
    if (adeddAddressesItem) {
      _stopAddressesItems[stopIndex] = [
        ..._stopAddressesItems[stopIndex],
        adeddAddressesItem,
      ];
    }
  });
  return _stopAddressesItems;
});

const stopExcludedLegs = computed(function () {
  const excludedLegs = props.airRoadBookingJob?.exclude_legs || [];
  return {
    '1': excludedLegs.includes(1),
    '2': excludedLegs.includes(2),
  };
});

let selectedStop: ReadonlyDeep<
  openapi.AirroadJobDetailedContentOpenapi['stops'][0]
>;

function handleAddNewAddressClick(
  stop: ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi['stops'][0]>,
): void {
  selectedStop = stop;
  openAddAddressDialog();
}

const adeddAddressesItems = ref<(AddressItem | undefined)[]>([]);

function _buildAddressItem(
  // address: AddAddressDialogFormValidData,
  address: openapi.AddressBookAddressesListContentOpenapi['results'][0],
  stop: ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi['stops'][0]>,
): AddressItem {
  return {
    value: {
      address_line_1: address.line1 || '',
      address_line_2: address.line2 || '',
      name: address.name || '',
      // TODO is here should be number or object
      // @ts-expect-error
      original_stop: stop.id || NaN,
      postcode: address.suburb?.postcode || '',
      state: address.suburb?.state || '',
      stop_number: stop.stop_number,
      suburb: address.suburb?.name || '',
    },
    title: (address.name || '') + ', ' + (address.suburb?.state || ''),
  };
}

async function addAddressDialogConfirm(
  formData: AddAddressDialogFormValidData,
): Promise<void> {
  const stopIndex = selectedStop.stop_number - 1;
  adeddAddressesItems.value[stopIndex] = undefined;
  const items = stopAddressesItems.value[stopIndex];
  const existedAddress = items.find(function (item) {
    const itemValue = item.value;
    if (
      formData.line1 === itemValue.address_line_1 &&
      formData.line2 === itemValue.address_line_2 &&
      formData.name === itemValue.name &&
      formData.suburb?.name === itemValue.suburb &&
      formData.suburb?.postcode === itemValue.postcode &&
      formData.suburb?.state === itemValue.state
    ) {
      return true;
    }
    return false;
  });
  if (!existedAddress) {
    const address: openapi.AddressGETOpenapi = {
      id: NaN,
      name: formData.name || '',
      line1: formData.line1,
      line2: formData.line2,
      suburb: formData.suburb,
      warehouse: false,
      note_text: '',
      note_name: '',
      note_phone: '',
      is_default: false,
    };
    adeddAddressesItems.value[stopIndex] = _buildAddressItem(
      address,
      selectedStop,
    );
  }
  const addressValue = adeddAddressesItems.value[stopIndex]?.value;
  if (addressValue) {
    model.value.data.ar_stops[stopIndex] = addressValue;
  }
}

function openAddAddressDialog() {
  const dialogName: DialogName = 'addAddress';
  dialogStore.open(dialogName, {
    confirm: addAddressDialogConfirm,
  });
}
</script>

<template>
  <section
    :class="$style.legChooseStepNewAirRoadSection"
    data-testid="legChooseStepNewAirRoadSection"
  >
    <StepNewAirRoadHeader>
      <h1>Job #{{ props.airRoadBookingJob?.rockhopper_id }}</h1>
    </StepNewAirRoadHeader>
    <LegChooseNewAirRoadStepAdditionalInfoDescList
      :job="props.airRoadBookingJob"
      class="mb-4"
    />
    <WideJobStopEmptyDiagram
      :stop-count="stops.length"
      :class="$style.diagram"
    />
    <div :class="$style.stopAddresses">
      <div
        v-for="(stop, stopIndex) in stops"
        :key="stopIndex"
        :class="$style.stopAddress"
      >
        <div :class="$style.addressFieldAndBtn">
          <div>
            <KSingleAutocompleteField
              v-model="model.data.ar_stops[stopIndex]"
              :items="stopAddressesItems[stopIndex]"
              label="Address"
              :rules="[requiredValidationRule]"
              hide-details
              :disabled="
                stops.length === 3 &&
                ((stopIndex === 0 && model.data.ar_booked_leg === 2) ||
                  (stopIndex === 2 && model.data.ar_booked_leg === 1))
              "
              :class="$style.addressField"
            />
          </div>
          <div>
            <KBtn
              variant="text"
              append-icon="mdi-plus"
              :class="$style.addNewAddressBtn"
              @click="handleAddNewAddressClick(stop)"
            >
              Add new address
            </KBtn>
          </div>
        </div>
        <NewAirRoadStopAddressText
          :class="$style.addressText"
          :stops="stops"
          :ar-stops="model.data.ar_stops"
          :stop-index="stopIndex"
        />
      </div>
    </div>

    <div v-if="stops.length === 3" :class="$style.legToggles">
      <v-radio-group
        v-model="model.data.ar_booked_leg"
        inline
        hide-details
        :class="$style.legRadioGroup"
      >
        <KRadioField label="Leg 1" :value="1" :disabled="stopExcludedLegs[1]" />
        <KRadioField label="Leg 2" :value="2" :disabled="stopExcludedLegs[2]" />
      </v-radio-group>
    </div>

    <LegProductsTablesSection v-if="stops.length" :stops="stops" />
  </section>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.legChooseStepNewAirRoadSection {
}
  .legToggles {
    margin: 24px 0 0 0;
  }
    .legRadioGroup {
      margin-left: 24px;
      :global {
        .v-input__control {
          align-items: center;
        }
      }
    }
  .diagram {
    margin: 20px 0 12px 0;
  }
  .stopAddresses {
    display: flex;
  }
    .stopAddress {
      width: 100%;
      padding-right: 12px;
    }
      .addressFieldAndBtn {
        display: flex;
        align-items: center;
      }
        .addressField {
          width: 258px;
          :global {
            .v-field__input {
              padding-right: 0;
            }
          }
        }
        .addNewAddressBtn {
          text-transform: none;
          :global {
            .v-btn__append {
              margin-top: 1px;
            }
          }
        }
      .addressText {
        padding-top: 5px;
      }
</style>
